import { forwardRef, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import { useSupportChatDE } from '~/pages/DubaiExpress/hooks/useSupportChatDE';

import { ArrowBackIcon, SupportAgentIcon } from './icons';

interface PageHeaderDEProps {
  title: ReactNode;
  showBackButton?: boolean;
  backButtonText?: string;
  showSupportButton?: boolean;
  onBackClick: () => void;
  isMobile: boolean;
  className?: string;
}

/**
 * Page header of the DubaiExpress cabinet mockup: on desktop a 1248px bar with
 * a text back link, the centred title and the orange support pill; on mobile
 * a 46px bar with the back arrow, the title and the support icon.
 */
export const PageHeaderDE = forwardRef<HTMLHeadingElement, PageHeaderDEProps>(
  (
    {
      title,
      showBackButton = true,
      backButtonText,
      showSupportButton = false,
      onBackClick,
      isMobile,
      className,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { openSupportChat } = useSupportChatDE('page_header');
    const headerRef = ref as Ref<HTMLElement>;

    if (isMobile) {
      return (
        <header
          ref={headerRef}
          className={cn(
            'grid h-[46px] grid-cols-[44px_minmax(0,1fr)_44px] items-center rounded-none! border-0 border-[#E4E3E3] border-b border-solid bg-white px-2 text-[#131316] [font-family:var(--fontFamily2)]',
            className,
          )}
        >
          {showBackButton ? (
            <button
              type="button"
              onClick={onBackClick}
              aria-label={t('back')}
              className="flex size-11 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[#131316]"
            >
              <ArrowBackIcon className="size-6" />
            </button>
          ) : (
            <span />
          )}
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-center font-semibold text-[18px] leading-6">
            {title}
          </span>
          {showSupportButton ? (
            <button
              type="button"
              onClick={openSupportChat}
              aria-label={t('support')}
              className="flex size-11 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[#131316]"
            >
              <SupportAgentIcon className="size-6" />
            </button>
          ) : (
            <span />
          )}
        </header>
      );
    }

    return (
      <header ref={headerRef} className={cn('bg-white', className)}>
        <div className="relative mx-auto flex h-20 w-full max-w-[1248px] items-center justify-between px-6 text-[#131316] [font-family:var(--fontFamily2)]">
          {showBackButton ? (
            <button
              type="button"
              onClick={onBackClick}
              className="flex flex-none cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-[#131316]! text-[16px] transition-colors duration-150 [font-family:inherit] hover:text-[#5B5657]!"
            >
              <ArrowBackIcon className="size-5" />
              {backButtonText && <span>{backButtonText}</span>}
            </button>
          ) : (
            <span />
          )}
          <h1 className="absolute left-1/2 m-0 -translate-x-1/2 whitespace-nowrap font-medium text-[#131316]! text-[32px] leading-9">
            {title}
          </h1>
          {showSupportButton ? (
            <button
              type="button"
              onClick={openSupportChat}
              className="flex h-11 flex-none cursor-pointer items-center gap-2 rounded-full border-0 bg-[#E86430] px-5 font-semibold text-[16px] text-white transition-colors duration-150 [font-family:inherit] hover:bg-[#D4561F]"
            >
              <SupportAgentIcon className="size-5" />
              {t('support')}
            </button>
          ) : (
            <span />
          )}
        </div>
      </header>
    );
  },
);

PageHeaderDE.displayName = 'PageHeaderDE';
